<div class="p-3 md:p-6 h-full">
  <div class="h-full grid grid-cols-3 gap-6 bg-gray-50 dark:bg-secondary-800 rounded-xl p-3 md:p-6">
    <!-- Left Side - Workspace List -->
    <div class="col-span-3 lg:col-span-2 bg-white dark:bg-indigo-custom rounded-xl p-3 md:p-3 flex flex-col h-full overflow-hidden">
      <h2 class="font-semibold text-secondary mb-4 flex-shrink-0 p-3 pb-0">Select Workspace</h2>

      <!-- Scrollable Workspace List -->
      <div class="flex-1 overflow-y-auto custom-scroll p-3">
        <!-- Loading State -->
        <div *ngIf="loading$ | async" class="flex justify-around items-center h-full">
          <svg class="animate-spin h-6 w-6 text-blue-royal" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>

        <!-- Error State -->
        <div *ngIf="error$ | async as error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p class="text-red-600 dark:text-red-400 text-sm">{{ error }}</p>
        </div>

        <!-- Workspaces List -->
        <div *ngIf="!(loading$ | async)" class="space-y-3">
          <div *ngFor="let workspace of workspaces$ | async"
               class="flex gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer transition-colors duration-150"
               (click)="selectWorkspace(workspace)">
            <!-- Workspace Icon -->
            <div class="flex-shrink-0">
              <div [class]="'w-12 h-12 rounded-lg flex items-center justify-center text-white font-semibold text-lg bg-violet-500 shadow shadow-violet-200'">
                {{ workspace.initials }}
              </div>
            </div>

            <!-- Workspace Details -->
            <div class="flex-grow min-w-0">
              <div class="flex items-center justify-start space-x-3 mb-1">
                <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {{ workspace.name }}
                </h3>
                <span *ngIf="workspace.icp" class="text-xs bg-violet-500/10 text-violet-500 py-1 px-2 rounded-md">ICP: {{ workspace.icp.name }}</span>
              </div>
              <p class="text-xs text-gray-600 dark:text-gray-400 flex items-center space-x-2 mb-2">
                <span>{{ workspace.searches_count }} searches</span>
                <span class="text-[8px] text-gray-400">•</span>
                <span>Created {{ workspace.created_at | date:'medium' }}</span>
                <span class="text-[8px] text-gray-400">•</span>
                <span>{{ workspace.total_files }} files attached</span>
              </p>
              <p class="text-xs text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                {{ workspace.description }}
              </p>
              <div class="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-500">
                <span class="flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-3">
                    <path fill-rule="evenodd" d="M15.621 4.379a3 3 0 0 0-4.242 0l-7 7a3 3 0 0 0 4.241 4.243h.001l.497-.5a.75.75 0 0 1 1.064 1.057l-.498.501-.002.002a4.5 4.5 0 0 1-6.364-6.364l7-7a4.5 4.5 0 0 1 6.368 6.36l-3.455 3.553A2.625 2.625 0 1 1 9.52 9.52l3.45-3.451a.75.75 0 1 1 1.061 1.06l-3.45 3.451a1.125 1.125 0 0 0 1.587 1.595l3.454-3.553a3 3 0 0 0 0-4.242Z" clip-rule="evenodd" />
                  </svg>
                  {{ workspace.formatted_file_size }}
                </span>

                <span *ngIf="workspace.is_default" class="flex items-center gap-1 text-gray-500 bg-gray-200/50 px-3 rounded-full">
                  Default
                </span>

              </div>
            </div>

            <!-- Arrow Icon -->
            <div class="flex-shrink-0 flex items-center">
              <svg class="w-4 h-4 text-gray-400 dark:text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Empty State (shown when no workspaces) -->
        <div *ngIf="!(loading$ | async) && (workspaces$ | async)?.length === 0" class="flex flex-col items-center justify-center h-full py-12">
          <div class="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
            </svg>
          </div>
          <p class="text-gray-500 dark:text-gray-400">No workspaces available</p>
          <p class="text-sm text-gray-400 dark:text-gray-500 mt-1">Create your first workspace to get started</p>
        </div>
      </div>
    </div>

    <!-- Right Side - Create New Workspace -->
    <div class="col-span-3 lg:col-span-1 bg-white dark:bg-indigo-custom rounded-xl p-3 md:p-3 flex flex-col h-full overflow-hidden">
      <h2 class="font-semibold text-secondary mb-4 flex-shrink-0 p-3 pb-0">Create New Workspace</h2>

      <!-- Scrollable Form Container -->
      <div class="flex-1 overflow-y-auto custom-scroll p-3">
        <form #workspaceForm="ngForm" class="space-y-4" (ngSubmit)="createNewWorkspace($event)">
          <!-- Workspace Name -->
          <div>
            <label for="workspace-name" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Workspace Name <span class="text-red-500">*</span>
            </label>
            <input type="text"
                   id="workspace-name"
                   name="workspaceName"
                   [(ngModel)]="newWorkspace.name"
                   class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white placeholder-gray-400"
                   placeholder="Enter workspace name"
                   [disabled]="creatingWorkspace"
                   required>
          </div>

          <!-- Description -->
          <div>
            <label for="workspace-description" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Add a description of your new workspace</p>
            <textarea id="workspace-description"
                      name="workspaceDescription"
                      [(ngModel)]="newWorkspace.description"
                      rows="3"
                      class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white placeholder-gray-400 resize-none"
                      placeholder="Add a description"
                      [disabled]="creatingWorkspace"></textarea>
          </div>

          <!-- Instructions -->
          <div>
            <label for="workspace-instructions" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Instructions
            </label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Add instructions for your new workspace</p>
            <textarea id="workspace-instructions"
                      name="workspaceInstructions"
                      [(ngModel)]="newWorkspace.instructions"
                      rows="3"
                      class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white placeholder-gray-400 resize-none"
                      placeholder="Add instructions"
                      [disabled]="creatingWorkspace"></textarea>
          </div>

          <!-- ICP Selection -->
          <div>
            <label for="workspace-icp" class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Select ICP <span class="text-red-500">*</span>
            </label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">Choose an ICP to link with this workspace</p>
            <select id="workspace-icp"
                    name="icpId"
                    [(ngModel)]="newWorkspace.icpId"
                    class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                    [disabled]="creatingWorkspace || (icps$ | async)?.length === 0"
                    required>
              <option *ngFor="let icp of icps$ | async" [value]="icp.id">
                {{ icp.name }} {{ icp.is_default ? '(Default)' : '' }}
              </option>
            </select>

            <!-- Loading state for ICPs -->
            <div *ngIf="icpsLoading$ | async" class="mt-2 flex items-center text-xs text-gray-500">
              <svg class="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading ICPs...
            </div>

            <!-- No ICPs available -->
            <div *ngIf="!(icpsLoading$ | async) && (icps$ | async)?.length === 0" class="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-700 dark:text-amber-400">
              No ICPs available. Please <a routerLink="/icp/create" class="text-blue-600 dark:text-blue-400 hover:underline">create an ICP</a> first.
            </div>

            <!-- Warning message -->
            <div *ngIf="newWorkspace.icpId" class="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
              <div class="flex items-start">
                <svg class="h-3 w-3 text-blue-500 mt-0.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
                <p class="text-xs text-blue-700 dark:text-blue-400">
                  <strong>Important:</strong> Once you select an ICP for this workspace, you cannot change it later. This ICP will be used for all searches and insights within this workspace.
                </p>
              </div>
            </div>
          </div>

          <!-- Knowledge Base -->
          <div>
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Knowledge base
            </label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">Upload a file containing information about your project (max 10MB)</p>

            <!-- File Upload Area -->
            <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
              <input type="file"
                     id="file-upload"
                     name="files"
                     #fileInput
                     (change)="onFileSelected($event)"
                     class="hidden"
                     accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.pptx"
                     [disabled]="creatingWorkspace">

              <label for="file-upload" class="cursor-pointer">
                <!-- Cloud Upload Icon -->
                <svg class="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                </svg>

                <span class="text-blue-600 dark:text-blue-400 font-medium text-xs tracking-wide">
                  {{ selectedFiles.length > 0 ? 'Replace file' : 'Upload file' }}
                </span>
                <p class="text-xs text-gray-500 dark:text-gray-400 tracking-wide mt-1">{{ selectedFiles.length > 0 ? 'File selected' : 'No file selected' }}</p>
              </label>
            </div>

            <!-- Selected File -->
            <div *ngIf="selectedFiles.length > 0" class="mt-2">
              <div class="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs">
                <span class="text-gray-700 dark:text-gray-300 truncate">{{ selectedFiles[0].name }} ({{ formatFileSize(selectedFiles[0].size) }})</span>
                <button type="button"
                        (click)="removeFile(0)"
                        class="text-red-500 hover:text-red-700 text-xs"
                        [disabled]="creatingWorkspace">
                  Remove
                </button>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <button type="submit"
                  class="w-full bg-blue-600 text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-150 text-sm mt-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  [disabled]="creatingWorkspace || uploadingFiles || !newWorkspace.name?.trim() || !newWorkspace.icpId">
            <svg *ngIf="creatingWorkspace" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>{{ creatingWorkspace ? 'Creating...' : 'Create Workspace' }}</span>
          </button>
        </form>

        <!-- Upload Progress -->
        <div *ngIf="uploadingFiles" class="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p class="text-sm text-blue-700 dark:text-blue-400 mb-2">Uploading files...</p>
          <div class="space-y-1">
            <div *ngFor="let status of uploadStatus" class="text-xs flex items-center gap-2">
              <svg *ngIf="status.status === 'uploading'" class="animate-spin h-3 w-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg *ngIf="status.status === 'success'" class="h-3 w-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <svg *ngIf="status.status === 'error'" class="h-3 w-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
              <span [class.text-gray-600]="status.status === 'uploading'"
                    [class.text-green-600]="status.status === 'success'"
                    [class.text-red-600]="status.status === 'error'">
                {{ status.fileName }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
